import React, { useState } from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import { Box } from '@mui/material';
import AddIcon from "@mui/icons-material/Add"
import {lightBlue} from "@mui/material/colors";
import Grid from '@mui/system/Unstable_Grid';
import CircularProgress from '@mui/material/CircularProgress';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import AddTransferDialog from '../dialogs/AddTransferDialog.jsx';
import { addTransfer } from '../../../shared utils/transferApi.js';
import Snackbar from '@mui/material/Snackbar';
import MuiAlert from '@mui/material/Alert';

export default function MissingTransfersTable({
  campuses,
  refreshData,
  loading = false,
  handleSelectStudent,
  mFlights = [],
  internalTransfers = [],
}) {

  const [addDialog, setAddDialog] = useState({
    open: false,
    studentId: null,
    direction: "IN",
    date: null,
  });

  const [toastOpen, setToastOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastSeverity, setToastSeverity] = useState('success');
  const showToast = (message, severity = 'success') => {
    setToastMessage(message);
    setToastSeverity(severity);
    setToastOpen(true);
  };

  const coveredByInternal = new Set(
    internalTransfers.map(t => `${t.studentId}-${t.transferDate}`)
  );

  const isCovered = (studentId, date) =>
    coveredByInternal.has(`${studentId}-${date}`);

  const groupedByMissingDate = mFlights.reduce((acc, item) => {
    if (item.missingInbound && !isCovered(item.studentId, item.stayStartDate)) {
      const date = item.stayStartDate;
      if (!acc[date]) acc[date] = [];
        acc[date].push({ ...item, direction: 'IN' });
    }

    if (item.missingOutbound && !isCovered(item.studentId, item.stayEndDate)) {
      const date = item.stayEndDate;
      if (!acc[date]) acc[date] = [];
        acc[date].push({ ...item, direction: 'OUT' });
    }

    return acc;
  }, {});

  const [expanded, setExpanded] = useState({});

  const toggleDate = (date) => {
    setExpanded(prev => ({
      ...prev,
      [date]: !prev[date],
    }));
  };

  const handleAdd = (transfer, studentId) => {
    addTransfer(transfer, studentId)
    .then(() => {
      showToast('Transfer added successfully', 'success');
      refreshData?.();
    })
    .catch(() => {
      showToast('Failed to add transfer', 'error');
    });
  };

  const openAddDialog = (studentId, direction, date) => {
    if (!date) return;
    setAddDialog({ open: true, studentId, direction, date });
  };

  const closeAddDialog = () => {
    setAddDialog((prev) => ({ ...prev, open: false }));
  };

  return (
    <TableContainer component={Paper} sx={{margin: '0 auto',  maxWidth: 1900}}>
      <Grid sx={{ display: 'inline', alignContent: 'flex-start', position:'left', bgcolor:lightBlue[100]}}>
      </Grid>

      <Table sx={{minWidth: 800, color:lightBlue[200], margin: '0 auto'}} size="small" aria-label="simple table">
        <TableHead>
          <TableRow sx={{bgcolor:lightBlue[100], border:1.5}}>
            <TableCell sx={{border:1.5}} align="center" >Student ID</TableCell>
            <TableCell sx={{border:1.5}} align="center">MT Ref</TableCell>
            <TableCell sx={{border:1.5}} align="center">Name</TableCell>
            <TableCell sx={{border:1.5}} align="center">Campus</TableCell>
            <TableCell sx={{border:1.5}} align="center">Stay Start</TableCell>
            <TableCell sx={{border:1.5}} align="center">Stay End</TableCell>
            <TableCell sx={{border:1.5}} align="center">IN</TableCell>
            <TableCell sx={{border:1.5}} align="center">OUT</TableCell>
          </TableRow>
        </TableHead>

        <TableBody>
          {loading ? (
            <TableRow>
              <TableCell colSpan={8} align="center" sx={{ py: 6 }}>
                <CircularProgress />
              </TableCell>
            </TableRow>
          ) : (
            Object.entries(groupedByMissingDate).sort(([a], [b]) => a.localeCompare(b)).map(
              ([date, rows]) => (
                <React.Fragment key={date}>
                  <TableRow>
                    <TableCell colSpan={8} sx={{ bgcolor: lightBlue[50], fontWeight: 'bold' }}>
                      <IconButton onClick={() => toggleDate(date)}>
                        {expanded[date] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      </IconButton>
                        {date}
                    </TableCell>
                  </TableRow>

                {expanded[date] && rows.map((item, index) => (
                  <TableRow key={`${date}-${index}`}>
                    <TableCell
                      sx={{
                        border: 1.5,
                        textAlign: 'center',
                        cursor: 'pointer',
                        color: 'blue',
                        textDecoration: 'underline',
                      }}
                      onClick={() => handleSelectStudent(item.studentId)}
                    >
                      {item.studentId}
                    </TableCell>
                    <TableCell sx={{ border: 1.5, textAlign: 'center' }}>{item.mtRef}</TableCell>
                    <TableCell sx={{ border: 1.5, textAlign: 'center' }}>
                      {`${item.studentName} ${item.studentSurname}`}
                    </TableCell>
                    <TableCell sx={{ border: 1.5, textAlign: 'center' }}>{item.stayCampus}</TableCell>
                    <TableCell sx={{ border: 1.5, textAlign: 'center' }}>{item.stayStartDate}</TableCell>
                    <TableCell sx={{ border: 1.5, textAlign: 'center' }}>{item.stayEndDate}</TableCell>

                    {/* INBOUND */}
                    <TableCell sx={{ border: 1.5, textAlign: 'center', bgcolor: item.direction !== 'IN' ? lightBlue[50] : 'inherit' }}>
                      {item.direction === 'IN' ? (
                        <Box display="flex" alignItems="center" justifyContent="center" gap={0.5}>
                          <ErrorOutlineIcon sx={{ color: 'red', verticalAlign: 'middle' }} titleAccess="Missing IN" />
                          <IconButton
                            color="primary"
                            onClick={() => openAddDialog(item.studentId, "IN", new Date(item.stayStartDate))}
                            size="small"
                          >
                            <AddIcon />
                          </IconButton>

                        </Box>
                      ) : null}
                    </TableCell>

                    {/* OUTBOUND */}
                    <TableCell sx={{ border: 1.5, textAlign: 'center', bgcolor: item.direction !== 'OUT' ? lightBlue[50] : 'inherit' }}>
                      {item.direction === 'OUT' ? (
                        <Box display="flex" alignItems="center" justifyContent="center" gap={0.5}>
                          <ErrorOutlineIcon sx={{ color: 'red', verticalAlign: 'middle' }} titleAccess="Missing OUT" />
                          <IconButton
                            color="primary"
                            onClick={() => openAddDialog(item.studentId, "OUT", new Date(item.stayEndDate))}
                            size="small"
                          >
                            <AddIcon />
                          </IconButton>
                        </Box>
                      ) : null}
                    </TableCell>
                  </TableRow>
                ))}
              </React.Fragment>
            )
            )
          )
        }
        </TableBody>
      </Table>

      <AddTransferDialog
        open={addDialog.open}
        onClose={closeAddDialog}
        transferDate={addDialog.date}
        direction={addDialog.direction}
        studentId={addDialog.studentId}
        campuses={campuses}
        onSaved={(payload, studentId) => handleAdd(payload, studentId)}
      />

      <Snackbar
        open={toastOpen}
        autoHideDuration={4000}
        onClose={() => setToastOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <MuiAlert
          onClose={() => setToastOpen(false)}
          severity={toastSeverity}
          elevation={6}
          variant="filled"
          sx={{ width: '100%' }}
        > 
          {toastMessage}
        </MuiAlert>
      </Snackbar>
    </TableContainer>
  );
}

